/**
 * Zielen und Ausloesen von Skills (Masterprompt Abschnitte 82, 44).
 *
 * Zwei Bedienarten, ein Ergebnis:
 *
 * **Am Handy** - Skillbutton halten und ziehen: Die Schaltflaeche wird dabei
 * zum Zielstick (Produktentscheidung vom 24.09.2026). Nicht der Punkt unter
 * dem Finger zaehlt, sondern die AUSLENKUNG - Richtung und Entfernung. Ein
 * Daumen kommt sonst nicht weit genug, und er verdeckt ausgerechnet die
 * Stelle, auf die man zielt. Loslassen wirkt, zurueck zur Schaltflaeche bricht
 * ab. Ein kurzer Tipp nimmt den Skill in die Hand.
 *
 * **Am Desktop** - zwei Wege, beide erlaubt (Produktentscheidung vom
 * 24.09.2026):
 * - Schaltflaeche halten, ziehen, loslassen (wie am Handy).
 * - Skill "in die Hand nehmen": einmal auf die Schaltflaeche klicken oder die
 *   Taste druecken. Der Skill bleibt dann bereit, die Vorschau folgt der Maus,
 *   und der naechste Linksklick auf die Spielflaeche loest ihn aus. Abbrechen
 *   geht mit Escape, Rechtsklick oder demselben Skill noch einmal.
 *
 * Diese Klasse verwaltet nur den ZUSTAND des Zielens. Wo der Finger auf dem
 * Boden landet, rechnet das 3D-Bild aus - und ob der Skill ueberhaupt zustande
 * kommt, entscheidet ohnehin allein der Server.
 */

import type { Vec2, PlayerSide } from '@dueline/shared';
import { bildschirmZuSpiel } from './bildschirmRichtung.js';

/** Tastenbelegung der vier Skills am Desktop. */
export const SKILL_KEYS: Readonly<Record<string, number>> = {
  Digit1: 1,
  Digit2: 2,
  Digit3: 3,
  KeyR: 4,
};

/**
 * Ab wann ein Tastendruck als GEHALTEN gilt, in Millisekunden
 * (Produktentscheidung vom 25.10.2026).
 *
 * Robin wollte beide Wege: "Wie bisher mit Linksklick, nachdem man ihn
 * ausgewaehlt hat" - oder "einfach Loslassen der Skilltaste". Beides geht
 * nur, wenn sich Tippen und Halten unterscheiden lassen, und dafuer gibt es
 * am Brett nur ein Mass: die Zeit.
 *
 * Wer TIPPT, nimmt den Skill in die Hand und zielt in Ruhe - ausgeloest
 * wird mit dem Linksklick. Wer HAELT, zielt waehrenddessen und loest beim
 * Loslassen aus.
 *
 * Ein bewusster Tipp liegt unter 150 ms, ein Halten deutlich darueber.
 * TUNING_PLACEHOLDER.
 */
export const SKILL_TASTE_HALTEN_MS = 250;

/** Wurde die Taste gehalten - oder nur getippt? */
export function tasteGehalten(dauerMs: number): boolean {
  return dauerMs >= SKILL_TASTE_HALTEN_MS;
}

/**
 * Abstand in Bildschirmpunkten, ab dem ein Ziehen als Zielen gilt.
 * Darunter wird der Skill in Blickrichtung ausgeloest - ein kurzer Tipp
 * soll also immer funktionieren.
 */
const AIM_DEAD_ZONE = 18;

/** Der Zielvorgang eines Skills, waehrend er laeuft. */
export interface AimingState {
  slot: number;
  /**
   * Wird wie ein Stick gezielt (Finger) oder direkt auf den Punkt (Maus)?
   *
   * Am Desktop zeigt der Mauszeiger genau auf die Stelle - das ist dort
   * genauer. Am Handy waere es unbrauchbar, deshalb der Unterschied.
   */
  stick: boolean;
  /** Punkt, an dem die Beruehrung begann. */
  originX: number;
  originY: number;
  /** Aktuelle Position des Fingers oder Mauszeigers. */
  currentX: number;
  currentY: number;
  /** True, solange die Bewegung zu klein zum Zielen ist. */
  inDeadZone: boolean;
}

/** Ein Skill, der "in der Hand" liegt und auf seinen Zielklick wartet. */
export interface ArmedState {
  slot: number;
  /** Letzte bekannte Zeigerposition, oder null (dann gilt die Blickrichtung). */
  pointerX: number | null;
  pointerY: number | null;
}

export class SkillInput {
  private aiming: AimingState | null = null;
  private armed: ArmedState | null = null;
  private listeners = new Set<(state: AimingState | null) => void>();

  /** Der laufende Zielvorgang, falls einer aktiv ist. */
  getAiming(): AimingState | null {
    return this.aiming;
  }

  /** Der Skill, der gerade auf seinen Zielklick wartet. */
  getArmed(): ArmedState | null {
    return this.armed;
  }

  /**
   * Nimmt einen Skill in die Hand - oder legt ihn wieder weg, wenn derselbe
   * schon bereitliegt. Ein zweiter Skill loest den ersten ab.
   */
  arm(slot: number, pointerX: number | null = null, pointerY: number | null = null): void {
    if (this.armed?.slot === slot) {
      this.disarm();
      return;
    }
    this.armed = { slot, pointerX, pointerY };
    this.notify();
  }

  /** Legt den bereitliegenden Skill weg. */
  disarm(): void {
    if (!this.armed) return;
    this.armed = null;
    this.notify();
  }

  /** Meldet die aktuelle Zeigerposition, damit die Vorschau mitgeht. */
  moveArmed(pointerX: number, pointerY: number): void {
    if (!this.armed) return;
    this.armed = { ...this.armed, pointerX, pointerY };
    this.notify();
  }

  /**
   * Loest den bereitliegenden Skill aus.
   * @returns Der Skill samt Zielpunkt, oder null, wenn keiner bereitlag.
   */
  release(pointerX: number, pointerY: number): ArmedState | null {
    if (!this.armed) return null;
    const ergebnis = { ...this.armed, pointerX, pointerY };
    this.armed = null;
    this.notify();
    return ergebnis;
  }

  subscribe(listener: (state: AimingState | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Beginnt das Zielen fuer einen Skill. */
  begin(slot: number, screenX: number, screenY: number, stick = false): void {
    // Ein gehaltener Skill loest einen bereitliegenden ab.
    this.armed = null;
    this.aiming = {
      slot,
      stick,
      originX: screenX,
      originY: screenY,
      currentX: screenX,
      currentY: screenY,
      inDeadZone: true,
    };
    this.notify();
  }

  /** Aktualisiert die Zielrichtung. */
  move(screenX: number, screenY: number): void {
    if (!this.aiming) return;

    const abstand = Math.hypot(screenX - this.aiming.originX, screenY - this.aiming.originY);
    this.aiming = {
      ...this.aiming,
      currentX: screenX,
      currentY: screenY,
      inDeadZone: abstand < AIM_DEAD_ZONE,
    };
    this.notify();
  }

  /**
   * Beendet das Zielen.
   * @returns Der Zustand zum Zeitpunkt des Loslassens, oder null bei Abbruch.
   */
  end(): AimingState | null {
    const ergebnis = this.aiming;
    this.aiming = null;
    this.notify();
    return ergebnis;
  }

  /** Bricht das Zielen ohne Auswirkung ab. */
  cancel(): void {
    if (!this.aiming) return;
    this.aiming = null;
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this.aiming);
  }
}

/**
 * Groesste Auslenkung des Zielsticks in Bildschirmpunkten.
 *
 * Wer so weit zieht, zielt auf die volle Reichweite des Skills. Der Wert ist
 * bewusst groesser als beim Bewegungsjoystick (56): Beim Zielen kommt es auf
 * Feinheit an, beim Laufen nicht.
 */
export const AIM_STICK_RADIUS = 90;

/**
 * Zielpunkt aus der Auslenkung eines Zielsticks.
 *
 * Bildschirm und Spielfeld liegen unterschiedlich: Nach oben auf dem Schirm
 * heisst "Richtung Gegner", nach rechts heisst "nach rechts aus eigener
 * Sicht" - genau wie beim Bewegungsjoystick.
 *
 * WELCHE WELTRICHTUNG DAS IST, HAENGT AN DER SEITE (13.10.2026).
 *
 * Beide Spieler sehen ihre eigene Seite unten links; erreicht wird das
 * ueber die Kamera, die fuer Blau um 180 Grad gedreht steht. Derselbe
 * Daumenausschlag zeigt damit fuer die beiden Seiten in entgegengesetzte
 * Weltrichtungen - und genau das wird hier ausgeglichen, wie es
 * movementIntent fuer das Laufen tut.
 *
 * Bis zum 13.10.2026 fehlte das. Im Spiel auf dem Handy: "Die Bewegung war
 * gespiegelt und ich konnte mich normal bewegen. Was jedoch nicht
 * gespiegelt war, war die Richtung der Skills." Wer als Blau nach vorn
 * zielte, warf nach hinten.
 *
 * Die Seite ist deshalb ein PFLICHTFELD und hat keinen Vorgabewert: Ein
 * vergessener Aufruf soll nicht stillschweigend eine Seite bevorzugen,
 * sondern beim Uebersetzen auffallen.
 *
 * Seit dem 18.10.2026 zaehlt auch die Schraege der Kamera - wie beim
 * Laufen (Produktentscheidung, siehe bildschirmRichtung.ts).
 *
 * Die Laenge der Auslenkung bestimmt die Entfernung, hoechstens aber die
 * Reichweite des Skills.
 */
export function resolveStickTarget(
  heroPosition: Vec2,
  facing: number,
  deltaX: number,
  deltaY: number,
  range: number,
  /** Auf welcher Seite der gemeinsamen Lane der Spieler zuhause ist. */
  side: PlayerSide,
): Vec2 {
  const laenge = Math.hypot(deltaX, deltaY);

  /*
   * Kaum ausgelenkt: Dann zaehlt die Blickrichtung, damit ein Skill nie
   * versehentlich direkt vor den eigenen Fuessen landet.
   *
   * Hier wird NICHT gedreht: facing ist bereits eine Weltrichtung und
   * keine Bildschirmrichtung.
   */
  if (laenge < 1) {
    return {
      x: heroPosition.x + Math.cos(facing) * range,
      y: heroPosition.y + Math.sin(facing) * range,
    };
  }

  const anteil = Math.min(1, laenge / AIM_STICK_RADIUS);
  const entfernung = range * anteil;

  // Bildschirm: deltaX nach rechts, deltaY nach UNTEN.
  const richtung = bildschirmZuSpiel(deltaX / laenge, -deltaY / laenge, side);

  return {
    x: heroPosition.x + richtung.x * entfernung,
    y: heroPosition.y + richtung.y * entfernung,
  };
}

/**
 * Rechnet eine Zielrichtung in einen Zielpunkt auf der Spielebene um.
 *
 * @param heroPosition Wo der Hero gerade steht.
 * @param groundPoint  Der Punkt auf dem Boden unter dem Finger, falls bekannt.
 * @param facing       Blickrichtung des Heroes als Ersatz.
 * @param range        Maximale Zielentfernung des Skills.
 */
export function resolveAimTarget(
  heroPosition: Vec2,
  groundPoint: Vec2 | null,
  facing: number,
  range: number,
): Vec2 {
  if (groundPoint) return groundPoint;

  // Ohne brauchbaren Bodenpunkt wird in Blickrichtung gezielt. Der Server
  // macht dasselbe, wenn gar keine Richtung ankommt.
  return {
    x: heroPosition.x + Math.cos(facing) * range,
    y: heroPosition.y + Math.sin(facing) * range,
  };
}
